import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close as CloseIcon, Lock as LockIcon } from '@mui/icons-material';
import { updateUserProfile } from '../../services/api';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import {
  DASHBOARD_ACCENT,
  getProfileOutlinedButtonSx,
  getProfilePasswordFieldSx,
  getProfilePrimaryButtonSx,
  getProfileSectionIconSx,
  getProfileSurfaceTokens,
} from '../../utils/profilePageLayout';

const passwordInputProps = {
  autoComplete: 'new-password',
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  style: { fontSize: '16px' },
};

const passwordInputSx = (isDark) => {
  const base = getProfilePasswordFieldSx(isDark);
  return {
    ...base,
    '& .MuiOutlinedInput-root': {
      ...(base['& .MuiOutlinedInput-root'] || {}),
      minHeight: 50,
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '16px',
      py: 1.25,
      ...(base['& .MuiOutlinedInput-input'] || {}),
    },
    '& .MuiInputLabel-root': {
      fontSize: '16px',
      ...(base['& .MuiInputLabel-root'] || {}),
    },
    '& .MuiInputLabel-shrink': {
      fontSize: '0.78rem',
    },
  };
};

export default function ChangePasswordDialog({
  open,
  onClose,
  onSuccess,
  forceDark = false,
}) {
  const theme = useTheme();
  const isDark = forceDark || theme.palette.mode === 'dark';
  const tokens = getProfileSurfaceTokens(isDark);
  const isMobileShell = isMobileShellLayout();

  const [formData, setFormData] = useState({ password: '', confirm_password: '' });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setFormData({ password: '', confirm_password: '' });
    setFormErrors({});
    setSubmitError(null);
    setSaving(false);
  }, [open]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submitError) setSubmitError(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.password?.trim()) errors.password = 'Password is required';
    else if (formData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (!formData.confirm_password?.trim()) errors.confirm_password = 'Please confirm your password';
    else if (formData.password !== formData.confirm_password) errors.confirm_password = 'Passwords do not match';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) return;

    setSaving(true);
    setSubmitError(null);
    try {
      await updateUserProfile({ password: formData.password });
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err.errors) setFormErrors(err.errors);
      else setSubmitError(err.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  const surfaceSx = {
    bgcolor: isDark ? '#0b1220' : '#ffffff',
    backgroundImage: isDark
      ? `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.08)} 0%, #0b1220 100%)`
      : `linear-gradient(145deg, ${alpha(DASHBOARD_ACCENT, 0.06)} 0%, #ffffff 100%)`,
    border: `1px solid ${tokens.surfaceBorder}`,
  };

  const headerSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 2.5,
    pt: 2,
    pb: 1.5,
    borderBottom: `1px solid ${tokens.surfaceBorder}`,
    flexShrink: 0,
  };

  const actionsSx = {
    display: 'flex',
    px: 2.5,
    py: 2,
    pt: 0,
    gap: 1,
    flexDirection: isMobileShell ? 'column-reverse' : 'row',
    justifyContent: isMobileShell ? 'stretch' : 'flex-end',
    borderTop: isMobileShell ? `1px solid ${tokens.surfaceBorder}` : 'none',
    pb: 2,
    flexShrink: 0,
    '& > :not(:first-of-type)': isMobileShell ? { ml: '0 !important' } : undefined,
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={false}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      disableScrollLock
      disableAutoFocus
      disableRestoreFocus
      sx={
        isMobileShell
          ? {
              '& .MuiDialog-container': {
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                pt: 'calc(env(safe-area-inset-top) + 12px)',
                pb: 'calc(env(safe-area-inset-bottom) + 12px)',
              },
            }
          : undefined
      }
      PaperProps={{
        sx: {
          ...surfaceSx,
          borderRadius: '16px',
          m: isMobileShell ? 0 : 2,
          width: isMobileShell ? '100%' : undefined,
          maxWidth: isMobileShell ? 420 : undefined,
          maxHeight: isMobileShell
            ? 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px)'
            : '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: alpha('#000', isDark ? 0.72 : 0.45),
        },
      }}
    >
      <Box sx={headerSx}>
        <Box sx={getProfileSectionIconSx(isDark)}>
          <LockIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: tokens.text, fontSize: '1.05rem', lineHeight: 1.2 }}>
            Change Password
          </Typography>
          <Typography sx={{ color: tokens.subtleText, fontSize: '0.82rem', mt: 0.25 }}>
            Enter a new password with at least 8 characters.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close change password dialog"
          onClick={handleClose}
          disabled={saving}
          sx={{
            color: tokens.subtleText,
            '&:hover': { bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            flex: 1,
            minHeight: 0,
          }}
        >
          {submitError ? (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>
              {submitError}
            </Alert>
          ) : null}

          <TextField
            fullWidth
            label="New Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
            required
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...passwordInputProps,
              enterKeyHint: 'next',
            }}
            sx={passwordInputSx(isDark)}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="confirm_password"
            type="password"
            value={formData.confirm_password}
            onChange={handleInputChange}
            error={!!formErrors.confirm_password}
            helperText={formErrors.confirm_password}
            required
            autoComplete="new-password"
            InputLabelProps={{ shrink: true }}
            inputProps={{
              ...passwordInputProps,
              enterKeyHint: 'done',
            }}
            sx={passwordInputSx(isDark)}
          />
        </Box>

        <Box sx={actionsSx}>
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={saving}
            fullWidth={isMobileShell}
            sx={getProfileOutlinedButtonSx(isDark)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            fullWidth={isMobileShell}
            sx={getProfilePrimaryButtonSx()}
          >
            {saving ? 'Saving...' : 'Save Password'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
